import images from '@assets/images/index'
import { Button } from '@components/ui'
import { Col, Divider, Flex, Form, Row, Spin, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import {
  DragDropContext,
  DraggingStyle,
  DropResult,
  Droppable,
  NotDraggingStyle
} from 'react-beautiful-dnd'
import { MovePlayersPlayerWithTeamInfo } from 'src/types/type'

import {
  Entities_Team_MovePlayers_Player,
  Entities_Team_MovePlayers_Team,
  LoginId,
  SchoolId,
  TeamId,
  UserId
} from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { $SpecialGradeLevel, SpecialGradeLevel } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { Modal, SearchInput, Select } from '@suntex.fim/ui'

import PlayerListDisplay from './PlayerListDisplay'
import TeamList from './TeamList'

type GroupedPlayerList = Record<string, MovePlayersPlayerWithTeamInfo[]>
const inActiveTeamId = -2 as TeamId
const modelContent: Record<string, JSX.Element> = {
  Unassigned: (
    <p>
      Because of certain issues with roster data, it might not be possible to
      assign a student to one definite class automatically.
      <br />
      <br />
      When this happens, we put such students in this special,{' '}
      <strong>Unassigned</strong> team, by grade, and you can move them to the
      correct class from here.
      <br />
      <br />
      If you have a student in one of your classes incorrectly, you can also
      move that student to Unassigned, and another teacher or the principal will
      be able to move the student to their correct class.
    </p>
  ),
  Inactive: (
    <p>
      Because of certain issues with roster data, it might not be possible to
      assign a student to one definite class automatically or even a definite
      grade.
      <br />
      <br />
      When this happens, we put such students in this special,{' '}
      <strong>Inactive Students</strong> container, and you can move them to the
      correct class from here.
      <br />
      <br />
      Inactive students cannot log into First In Math. If you find that a
      student is in this container incorrectly, just move that student to the
      correct team, or even the Unassigned team for your grade, and this will
      activate the student.
    </p>
  ),
  Students: (
    <p>
      Enter student information in the search box to find matching students.
      Then you can drag a student from the search results to the team where that
      student belongs.
      <br />
      <br />
      You can also drag a whole team from the Teams panel on the right to the
      Students panel on the left, and then move students on that team.
      <br />
      <br />
      You can also click on a team to see the students on that team.
    </p>
  )
}

// Filter the record based on search value
function filterTeamAndPlayer<T extends { grade?: number }>(
  array: T[],
  searchValue: string,
  key?: string
): T[] {
  if (key) {
    if (Number(searchValue) > SpecialGradeLevel.Grade8) {
      return array.filter(
        (obj) => obj.grade && obj.grade > SpecialGradeLevel.Grade8
      )
    }
    return array.filter((obj) => obj.grade?.toString() === searchValue)
  }
  return array.filter((obj) => {
    return Object.keys(obj).some((key) => {
      const value = obj[key as keyof T]
      return String(value)
        .toLowerCase()
        .includes(searchValue.toString().toLowerCase())
    })
  })
}

function groupUsersByTeam(
  users: MovePlayersPlayerWithTeamInfo[]
): GroupedPlayerList {
  return users.reduce(
    (groupedUsers: GroupedPlayerList, user: MovePlayersPlayerWithTeamInfo) => {
      const team = user.teamName || ''
      const existing = groupedUsers[team] ?? []
      groupedUsers[team] = [user, ...existing]
      return groupedUsers
    },
    {}
  )
}

const findPlayersWithTeamId = (data: GroupedPlayerList, teamId: number) => {
  if (!data) {
    return []
  }
  return Object.keys(data)
    .map((key) => data[key].filter((entry) => entry.teamId === teamId))
    .flat()
}

export interface MovePlayersBoardProps {
  /** Scope id used by player-deactivation calls. Comes from the URL scope, not auth. */
  schoolId: SchoolId
  teams: Entities_Team_MovePlayers_Team[]
  isLoading: boolean
  reload: () => void
}

/**
 * Renders the move-players board (drag/drop UI, modals, search, grade filter).
 * Data is supplied by the caller — see TeamMovePlayers / SchoolMovePlayers.
 *
 * The page wrappers pass a `key` (team or school id) so React remounts the
 * board when the scope changes; reloads of the same scope keep selection.
 */
function MovePlayersBoard({
  schoolId,
  teams: incomingTeams,
  isLoading,
  reload
}: MovePlayersBoardProps) {
  const { api } = useFramework()
  const { login } = useAuth(isAuthenticated)

  const [movePlayerList, setMovePlayerList] = useState<
    Entities_Team_MovePlayers_Team[]
  >([])

  useEffect(() => {
    setMovePlayerList(incomingTeams)
  }, [incomingTeams])

  const addPlayer = useCallback(
    (teamId: TeamId, player: Entities_Team_MovePlayers_Player) => {
      setMovePlayerList((prev) =>
        prev.map((t) =>
          t.id === teamId && t.players
            ? { ...t, players: [...t.players, player] }
            : t
        )
      )
    },
    []
  )

  const removePlayer = useCallback(
    (teamId: TeamId, player: Entities_Team_MovePlayers_Player) => {
      setMovePlayerList((prev) =>
        prev.map((t) =>
          t.id === teamId && t.players
            ? { ...t, players: t.players.filter((p) => p.id !== player.id) }
            : t
        )
      )
    },
    []
  )

  const [selectedTeamId, setSelectedTeamId] = useState<TeamId>()
  const [selectedTeam, setSelectedTeam] =
    useState<Entities_Team_MovePlayers_Team | null>(null)
  const [selectedGrade, setSelectedGrade] = useState<SpecialGradeLevel>(
    SpecialGradeLevel.AllGrades
  )
  const [teamInfoModal, setTeamInfoModal] = useState<boolean>(false)
  const [playerDisplay, setPlayerDisplay] = useState<boolean>(false)
  const [contentType, setContentType] = useState<string>()
  const [assignedTeamList, setAssignedTeamList] = useState<
    Entities_Team_MovePlayers_Team[]
  >([])
  const [unAssignedTeamList, setunAssignedTeamList] = useState<
    Entities_Team_MovePlayers_Team[]
  >([])
  const [playerList, setPlayerList] = useState<GroupedPlayerList>({})
  const [selectedTeamPlayerList, setSelectedTeamPlayerList] = useState<
    Entities_Team_MovePlayers_Player[] | undefined
  >([])
  const [searchTeamQuery, setSearchTeamQuery] = useState<string>('')
  const [searchPlayerQuery, setSearchPlayerQuery] = useState<string>('')
  const [open, setOpen] = useState(false)
  const [modalText, setModalText] = useState<React.ReactNode>('')
  const [fromTeamId, setFromTeamId] = useState(0 as TeamId)
  const [toTeamId, setToTeamId] = useState(0 as TeamId)
  const [userId, setUserId] = useState('' as UserId)
  const [loginId, setLoginId] = useState(0 as LoginId)
  const [player, setPlayer] = useState<MovePlayersPlayerWithTeamInfo[]>([])
  const [original, setOriginal] = useState(false)
  const [teamLoading, setTeamLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(false)
  const handleOk = () => {
    setOpen(false)
  }

  const separateTeamList = (
    modifyTeamList: Entities_Team_MovePlayers_Team[]
  ) => {
    const assignedTeam: Entities_Team_MovePlayers_Team[] = []
    const unAssignedTeam: Entities_Team_MovePlayers_Team[] = []
    modifyTeamList.forEach((team) => {
      if (team.teacherName) {
        assignedTeam.push(team)
      } else {
        unAssignedTeam.push(team)
      }
    })
    setAssignedTeamList(assignedTeam)
    setunAssignedTeamList(unAssignedTeam)
  }
  const handleGradeChange = (value: SpecialGradeLevel) => {
    if (value < SpecialGradeLevel.AllGrades) {
      const modifyTeamList = filterTeamAndPlayer(
        movePlayerList,
        value.toString(),
        'grade'
      )
      if (searchTeamQuery.length > 0) {
        const finalList = filterTeamAndPlayer(modifyTeamList, searchTeamQuery)
        separateTeamList(finalList)
      } else {
        separateTeamList(modifyTeamList)
      }
    } else {
      separateTeamList(movePlayerList)
    }
    setSelectedGrade(value)
  }
  const changePlayerList = (players: MovePlayersPlayerWithTeamInfo[]) => {
    const groupedUsers = groupUsersByTeam(players)
    setPlayerList(groupedUsers)
  }

  const getPlayersOfSelectedTeam = (
    teamId = selectedTeam?.id,
    dragTeam = false
  ) => {
    const playerList = movePlayerList.filter((team) => team.id === teamId)
    setSelectedTeamPlayerList(playerList[0]?.players)
    if (dragTeam) {
      const PlayerList: MovePlayersPlayerWithTeamInfo[] = []
      playerList.forEach((team) => {
        if (team.players) {
          const players = team.players.map((player) => ({
            teamId: team.id,
            teamName: team.className,
            ...player
          }))
          PlayerList.push(...players)
        }
        return false
      })
      changePlayerList(PlayerList)
    }
  }

  useEffect(() => {
    separateTeamList(movePlayerList)
  }, [movePlayerList])

  useEffect(() => {
    if (searchTeamQuery.length > 0) {
      const modifyTeamList = filterTeamAndPlayer(
        movePlayerList,
        searchTeamQuery
      )
      if (
        selectedGrade !== SpecialGradeLevel.AllGrades &&
        selectedGrade !== SpecialGradeLevel.Unassigned
      ) {
        const finalList = modifyTeamList.filter(
          (item) => item.grade === selectedGrade
        )
        separateTeamList(finalList)
      } else {
        separateTeamList(modifyTeamList)
      }
    } else if (selectedGrade === SpecialGradeLevel.AllGrades) {
      separateTeamList(movePlayerList)
    } else {
      separateTeamList(
        movePlayerList.filter((item) => item.grade === selectedGrade)
      )
    }
  }, [searchTeamQuery])

  useEffect(() => {
    if (searchPlayerQuery.length > 0) {
      const PlayerList: MovePlayersPlayerWithTeamInfo[] = []
      movePlayerList.forEach((team) => {
        if (team.players) {
          const players = team.players.map((player) => ({
            grade: team.grade,
            teamId: team.id,
            teamName: team.className,
            ...player
          }))
          PlayerList.push(...players)
        }
        return false
      })
      const modifySearchPlayerQuery = searchPlayerQuery
        .replace(/\s+/g, '')
        .trim()
      const modifyPlayerList = filterTeamAndPlayer(
        PlayerList,
        modifySearchPlayerQuery
      )
      changePlayerList(modifyPlayerList)
    } else {
      getPlayersOfSelectedTeam(selectedTeamId, true)
    }
  }, [searchPlayerQuery])

  useEffect(() => {
    if (selectedTeam) {
      getPlayersOfSelectedTeam()
    }
  }, [selectedTeam])
  useEffect(() => {
    getPlayersOfSelectedTeam(selectedTeamId, true)
  }, [selectedTeamId])
  const options = $SpecialGradeLevel.listOptions()

  const makePlayerInactive = async (
    player: MovePlayersPlayerWithTeamInfo[],
    targetTeamName: string | undefined
  ) => {
    const sourceTeam = movePlayerList.find(
      (item) => item.id === (player[0].teamId ?? fromTeamId)
    )
    if (!sourceTeam) {
      message.error('Could not find the player\u2019s team.')
      return
    }
    const { grade } = sourceTeam
    setTeamLoading(true)
    try {
      await api.post_Player_AssignToSchool({
        playerId: player[0].id,
        schoolId,
        grade: $SpecialGradeLevel.from(grade),
        year: login.year
      })
      const filterPlayerList = playerList ? Object.values(playerList)[0] : []
      if (searchPlayerQuery.length === 0) {
        const updatedFilterPlayerList = filterPlayerList.filter(
          (item) => item.id !== player[0].id
        )
        changePlayerList(updatedFilterPlayerList)
      }
      if (searchPlayerQuery.length > 0) {
        setSearchPlayerQuery('')
      }
      delete player[0].teamId
      delete player[0].teamName
      addPlayer(inActiveTeamId, player[0])
      removePlayer(targetTeamName === 'undo' ? toTeamId : fromTeamId, player[0])
      if (targetTeamName === 'undo') {
        const updatePlayer = {
          ...player[0],
          teamId: Object.values(filterPlayerList)[0].teamId,
          teamName: Object.values(filterPlayerList)[0].teamName
        }
        filterPlayerList.push(updatePlayer)
        changePlayerList(filterPlayerList)
      }
      setTeamLoading(false)
      setModalText(
        'This player has been deactivated. You can activate them by moving them to any valid team.'
      )
      setOpen(true)
      setOriginal(true)
      reload()
    } catch (error) {
      message.error(error as string)
    }
  }
  const getModalText = (targetTeamName: string | undefined) => {
    if (targetTeamName?.length === 0) {
      return 'Unassigned'
    }
    if (targetTeamName === 'undo') {
      return 'their original team'
    }
    return targetTeamName
  }

  const movePlayer = async (
    fromTeamId: TeamId,
    toTeamId: TeamId,
    userId: UserId,
    loginId: LoginId,
    player: MovePlayersPlayerWithTeamInfo[],
    targetTeamName?: string
  ) => {
    setTeamLoading(true)
    if (toTeamId < 0) {
      await makePlayerInactive(player, targetTeamName)
      return
    }
    const body = {
      fromTeamId,
      toTeamId,
      userId,
      loginId
    }
    try {
      const response = await api.post_Team_MovePlayer(body)
      setModalText(
        <>
          <b>{response.userId}</b> has been moved to{' '}
          <b>{getModalText(targetTeamName)}.</b>
        </>
      )
      setOpen(true)
      setOriginal(false)
      const filterPlayerList = playerList ? Object.values(playerList)[0] : []
      if (targetTeamName === 'undo') {
        setOriginal(true)
        setModalText(
          <>
            <b>{response.userId}</b> is moved back to their original team.
          </>
        )
        if (searchPlayerQuery.length === 0 && filterPlayerList) {
          const updatePlayer = {
            ...player[0],
            teamId: Object.values(filterPlayerList)[0].teamId,
            teamName: Object.values(filterPlayerList)[0].teamName
          }
          filterPlayerList.push(updatePlayer)
          changePlayerList(filterPlayerList)
        }
        if (searchPlayerQuery.length > 0) {
          setSearchPlayerQuery('')
        }
      }
      if (targetTeamName !== 'undo' && searchPlayerQuery.length === 0) {
        const updatedFilterPlayerList = filterPlayerList.filter(
          (item) => item.id !== player[0].id
        )
        changePlayerList(updatedFilterPlayerList)
      }
      if (searchPlayerQuery.length > 0) {
        setSearchPlayerQuery('')
      }
      delete player[0].teamId
      delete player[0].teamName
      addPlayer(toTeamId, player[0])
      removePlayer(fromTeamId, player[0])
      setTeamLoading(false)
      reload()
    } catch (error) {
      setTeamLoading(false)
      message.error(error as string)
    }
  }

  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result
    if (!destination) return
    const droppedInSamePosition =
      source.droppableId === destination.droppableId &&
      source.index === destination.index

    if (source.droppableId === 'player-list') {
      if (!droppedInSamePosition) {
        setErrorMessage(false)
        const toTeamId = Number(destination.droppableId) as TeamId
        const fromTeamId = source.index as TeamId
        const playersWithTeamId = findPlayersWithTeamId(playerList, fromTeamId)
        const player = playersWithTeamId.filter(
          (player) => String(player.id) === draggableId
        )
        const targetTeamName = String(
          movePlayerList.filter((item) => item.id === toTeamId)[0].name
        )
        const userId = player[0]?.userId ?? ('' as UserId)
        const loginId = player[0]?.loginId ?? (0 as LoginId)
        setFromTeamId(fromTeamId)
        setToTeamId(toTeamId)
        setUserId(userId)
        setLoginId(loginId)
        setPlayer(player)
        if (toTeamId < 0) {
          await makePlayerInactive(player, targetTeamName)
          return
        }
        await movePlayer(
          fromTeamId,
          toTeamId,
          userId,
          loginId,
          player,
          targetTeamName
        )
      } else {
        setModalText(<b>You can only move players between teams.</b>)
        setOpen(true)
        setOriginal(true)
        setErrorMessage(true)
      }
    } else if (destination.droppableId === 'player-list') {
      setSelectedTeamId(Number(draggableId) as TeamId)
    }
  }

  const getItemStyle = (
    isDragging: boolean,
    draggableStyle: DraggingStyle | NotDraggingStyle | undefined
  ): React.CSSProperties => ({
    userSelect: 'none' as const,
    background: isDragging ? 'rgba(235, 247, 255, 1)' : '',
    borderColor: isDragging ? '#3FB2FF' : '',
    cursor: 'all-scroll',
    boxShadow: isDragging ? '0px 0px 4px 0px #3fb2ff' : '',
    ...draggableStyle
  })

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Flex vertical gap="20px">
        {isLoading ? (
          <Spin />
        ) : (
          <>
            <div>
              <h3>Move Players</h3>
              <p className="leading-1">
                Drag and drop players to other sections
              </p>
            </div>
            <Flex className="move-players">
              <Droppable droppableId="player-list">
                {(provided, snapshot) => (
                  <>
                    <div
                      className="move-players__sidebar"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      <Flex vertical gap="10px">
                        <Form layout="vertical">
                          <Form.Item
                            colon={false}
                            label={
                              <Flex
                                align="center"
                                gap={10}
                                style={{ marginBottom: '8px' }}
                              >
                                Students{' '}
                                <images.InfoCircle
                                  onClick={() => {
                                    setContentType('Students')
                                    setTeamInfoModal(true)
                                  }}
                                />
                              </Flex>
                            }
                          >
                            <SearchInput
                              value={searchPlayerQuery}
                              onChange={setSearchPlayerQuery}
                            />
                          </Form.Item>
                        </Form>
                        <div className="player-list-container">
                          <PlayerListDisplay
                            playerList={playerList}
                            searchPlayerQuery={searchPlayerQuery}
                            snapshot={snapshot}
                            provided={provided}
                            getItemStyle={getItemStyle}
                          />
                        </div>
                      </Flex>
                    </div>
                    <div className="move-players__main">
                      <Flex
                        className="section-title move-players-titles"
                        align="center"
                        justify="space-between"
                      >
                        {!playerDisplay && (
                          <div>
                            <h4>Team Section</h4>
                            <p>Select a Team to view its players</p>
                          </div>
                        )}
                        {playerDisplay && (
                          <Flex align="center">
                            <Button
                              intent="icon"
                              onClick={() => {
                                setSelectedTeam(null)
                                setPlayerDisplay(false)
                              }}
                            >
                              <images.PreviousPrimaryArrow />
                            </Button>
                            <h5>
                              Team Section:{' '}
                              <span className="text-primary fw-bold">
                                {selectedTeam?.className}
                              </span>
                            </h5>
                          </Flex>
                        )}
                        {!playerDisplay && (
                          <Row gutter={10} align="bottom">
                            <Col xs={24} md={12} className="report-options">
                              <p className="dd-label fw-bold text-sm">Grades</p>
                              <Select.One
                                value={selectedGrade}
                                onChange={handleGradeChange}
                                options={options}
                                className="focus-mode-select"
                              />
                            </Col>
                            <Col xs={24} md={12}>
                              <SearchInput
                                value={searchTeamQuery}
                                onChange={setSearchTeamQuery}
                              />
                            </Col>
                          </Row>
                        )}
                      </Flex>
                      <div className="move-players__main-table">
                        <TeamList
                          Team={assignedTeamList}
                          playerDisplay={playerDisplay}
                          setSelectedTeam={setSelectedTeam}
                          setPlayerDisplay={setPlayerDisplay}
                          setSearchTeamQuery={setSearchTeamQuery}
                          teamLoading={teamLoading}
                          selectedTeamId={selectedTeamId}
                        />
                        {!playerDisplay && unAssignedTeamList.length > 0 && (
                          <Divider />
                        )}
                        <TeamList
                          Team={unAssignedTeamList}
                          playerDisplay={playerDisplay}
                          setSelectedTeam={setSelectedTeam}
                          setPlayerDisplay={setPlayerDisplay}
                          setContentType={setContentType}
                          setTeamInfoModal={setTeamInfoModal}
                          setSearchTeamQuery={setSearchTeamQuery}
                          teamLoading={teamLoading}
                          selectedTeamId={selectedTeamId}
                        />
                      </div>
                      {playerDisplay && (
                        <div className="move-players__main-list">
                          <Row gutter={[10, 10]} className="player-list">
                            {selectedTeamPlayerList &&
                            selectedTeamPlayerList.length > 0 ? (
                              selectedTeamPlayerList.map((player) => (
                                <Col xs={24} md={12} lg={8} key={player.id}>
                                  <div className="player-tiles">
                                    <h6>{player.displayName ?? '<No Name>'}</h6>
                                    <Flex justify="space-between">
                                      <Flex align="center" gap="4px">
                                        <div>
                                          <images.Hashtag />
                                        </div>
                                        <p className="text-chalkboard-txt text-sm">
                                          {player.userId ?? 'None'}
                                        </p>
                                      </Flex>
                                      <Flex align="center" gap="4px">
                                        <div>
                                          <images.ProfileCircle />
                                        </div>
                                        <p className="text-chalkboard-txt text-sm">
                                          {player.idNumber ?? 'None'}
                                        </p>
                                      </Flex>
                                    </Flex>
                                  </div>
                                </Col>
                              ))
                            ) : (
                              <strong style={{ marginLeft: '20px' }}>
                                No students found in this team
                              </strong>
                            )}
                          </Row>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Droppable>
            </Flex>
            <Modal
              closable={false}
              centered
              open={teamInfoModal}
              onOk={() => setTeamInfoModal(false)}
              onCancel={() => setTeamInfoModal(false)}
              width={900}
              className="current-ranking-info"
              footer={[
                <Button
                  shape="round"
                  intent="tertiary"
                  key="submit"
                  onClick={() => setTeamInfoModal(false)}
                >
                  Okay
                </Button>
              ]}
            >
              {modelContent[`${contentType}`]}
            </Modal>
            <Modal
              closable={false}
              open={open}
              footer={null}
              onCancel={handleOk}
              className={
                errorMessage
                  ? 'move-player-confirmation--error'
                  : 'move-player-confirmation'
              }
            >
              <Flex align="center" wrap gap={8}>
                <span className="modal-desc">{modalText}</span>
                <div className="modal-cta">
                  <Button
                    className="button button-small button-secondary"
                    onClick={handleOk}
                  >
                    OK
                  </Button>
                  {!original && (
                    <Button
                      className="button button-small button-secondary"
                      onClick={() => {
                        movePlayer(
                          toTeamId,
                          fromTeamId,
                          userId,
                          loginId,
                          player,
                          'undo'
                        )
                        setOriginal(true)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          movePlayer(
                            toTeamId,
                            fromTeamId,
                            userId,
                            loginId,
                            player,
                            ''
                          )
                          setOriginal(true)
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      Undo
                    </Button>
                  )}
                </div>
              </Flex>
            </Modal>
          </>
        )}
      </Flex>
    </DragDropContext>
  )
}

export default MovePlayersBoard
